'use strict';

// Shared design tokens. OZIRA's brand is black, red, and white -- the
// established identity (matches ozira-mobile/src/theme.js's original
// values). A same-session detour briefly rebranded this to a warm-cream/
// terracotta palette from a design canvas mockup; that was reverted on
// explicit correction -- the mockup's LAYOUT (spacing, component structure)
// is still worth using, but its color palette is not this brand.
//
// Framework-agnostic on purpose: plain numbers/hex strings. Web/Desktop
// map these to CSS custom properties; Mobile maps them into a React Native
// StyleSheet-compatible object (as ozira-mobile/src/theme.js already does).
// This module does not import React, React Native, or any CSS-in-JS library.

const brand = Object.freeze({
  primary: '#DB141A',
  primaryHover: '#C11216',
  primaryActive: '#A00F13',
  secondary: '#FF3D71',
  accent: '#7C3AED',
  success: '#1FA855',
  gold: '#C8930A',
  danger: '#E5484D',
  white: '#FFFFFF',
});

const darkColors = Object.freeze({
  ...brand,
  bg: '#0B0B12',
  surface: '#161622',
  card: '#1C1C28',
  cardAlt: '#22222F',
  text: '#F2F2F7',
  muted: '#8A8A99',
  border: '#2A2A38',
});

const lightColors = Object.freeze({
  ...brand,
  bg: '#F6F7F8',
  surface: '#FFFFFF',
  card: '#F6F7F8',
  cardAlt: '#F6F7F8',
  text: '#0E0F12',
  muted: '#6B707A',
  border: '#E0E3E8',
});

function getPalette(mode) {
  return mode === 'light' ? lightColors : darkColors;
}

// Spacing/radius scales are unchanged by the rebrand -- layout structure,
// not color, and no redesign spec called for a different scale.
const spacing = Object.freeze({ xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 });
const radius = Object.freeze({ sm: 8, md: 12, lg: 16, xl: 22, pill: 999 });

// Body font is Public Sans, matching the design canvas mockup's layout this
// session is porting over; `wordmark` is the Newsreader serif used only for
// the OZIRA brand name, never for body copy. Typography wasn't part of the
// color-brand correction -- only the palette above was reverted.
const typography = Object.freeze({
  fontFamily: {
    regular: "'Public Sans', ui-sans-serif, system-ui, sans-serif",
    medium: "'Public Sans', ui-sans-serif, system-ui, sans-serif",
    semibold: "'Public Sans', ui-sans-serif, system-ui, sans-serif",
    bold: "'Public Sans', ui-sans-serif, system-ui, sans-serif",
    wordmark: "'Newsreader', serif",
  },
  scale: Object.freeze({
    xs: 12, sm: 14, md: 16, lg: 18, xl: 22, xxl: 28, display: 36,
  }),
});

// Shadow alpha is heavier in dark mode so depth still reads on a dark
// surface (a light-mode shadow value is nearly invisible there).
const darkElevation = Object.freeze({
  none: 'none',
  sm: '0 1px 3px rgba(0, 0, 0, 0.28)',
  md: '0 4px 6px rgba(0, 0, 0, 0.28)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.32)',
});

const lightElevation = Object.freeze({
  none: 'none',
  sm: '0 1px 3px rgba(20, 20, 19, 0.06)',
  md: '0 4px 6px rgba(20, 20, 19, 0.06)',
  lg: '0 10px 15px rgba(20, 20, 19, 0.08)',
});

function getElevation(mode) {
  return mode === 'light' ? lightElevation : darkElevation;
}

// Kept as the dark-mode elevation for any caller (e.g. mobile) that imports
// a single mode-independent value directly instead of going through
// getElevation()/toCssVariables().
const elevation = darkElevation;

/** Flatten a palette + the shared scales into CSS custom-property pairs. */
function toCssVariables(mode) {
  const palette = getPalette(mode);
  const shadows = getElevation(mode);
  const out = {};
  for (const [key, value] of Object.entries(palette)) out[`--ozira-color-${kebab(key)}`] = value;
  for (const [key, value] of Object.entries(spacing)) out[`--ozira-space-${key}`] = `${value}px`;
  for (const [key, value] of Object.entries(radius)) out[`--ozira-radius-${key}`] = value === 999 ? '999px' : `${value}px`;
  for (const [key, value] of Object.entries(typography.scale)) out[`--ozira-font-size-${key}`] = `${value}px`;
  for (const [key, value] of Object.entries(shadows)) out[`--ozira-elevation-${key}`] = value;
  return out;
}

function kebab(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

module.exports = {
  brand,
  darkColors,
  lightColors,
  getPalette,
  spacing,
  radius,
  typography,
  elevation,
  getElevation,
  toCssVariables,
};
